import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion as Motion } from "framer-motion";

import api from "@/utils/axios";
import Loading from "@/components/Loading/Loading";

import cls from "./LoungeJoin.module.scss";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 220, damping: 22 },
  },
};

const fmtDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString();
};

const tone = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return "pending";
  if (s === "accepted") return "accepted";
  if (s === "rejected") return "rejected";
  return "neutral";
};

const LoungeJoin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clubId } = useParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [club, setClub] = useState(null);
  const [requests, setRequests] = useState([]);

  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | pending | accepted | rejected

  const clubName = club?.clubName || t("loungeJoin.clubFallback");

  const fetchList = async () => {
    try {
      setLoading(true);
      setErr("");

      const { data } = await api.get(
        `/v1/lounge/clubs/${clubId}/membership-requests`,
      );

      setClub(data?.club || null);
      setRequests(Array.isArray(data?.requests) ? data.requests : []);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return requests.filter((r) => {
      const s = String(r?.status || "").toLowerCase();
      if (filter !== "all" && s !== filter) return false;

      if (!term) return true;

      const uname = String(r?.sender?.username || "").toLowerCase();
      const dec = String(r?.decidedBy?.username || "").toLowerCase();
      return uname.includes(term) || dec.includes(term);
    });
  }, [requests, q, filter]);

  const counts = useMemo(() => {
    const c = { all: requests.length, pending: 0, accepted: 0, rejected: 0 };
    for (const r of requests) {
      const s = String(r?.status || "").toLowerCase();
      if (s === "pending") c.pending += 1;
      else if (s === "accepted") c.accepted += 1;
      else if (s === "rejected") c.rejected += 1;
    }
    return c;
  }, [requests]);

  const onBack = () => navigate(-1);

  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Header */}
        <Motion.header
          className={cls.header}
          variants={fadeUp}
          initial="hidden"
          animate="show"
        >
          <div className={cls.headerTop}>
            <div className={cls.headerLeft}>
              <span className={cls.kicker}>{t("loungeJoin.kicker")}</span>
              <h1 className={cls.title}>
                {t("loungeJoin.title", { clubName })}
              </h1>
              <p className={cls.subTitle}>{t("loungeJoin.subtitle")}</p>
            </div>

            <div className={cls.headerBtns}>
              <button
                type="button"
                className={`${cls.nuBtn} ${cls.nuBtnGhost}`}
                onClick={onBack}
              >
                {t("common.back")}
              </button>

              <Link
                to={`/lounge/clubs/single/${clubId}`}
                className={`${cls.nuBtn} ${cls.nuBtnInfo}`}
              >
                {t("loungeJoin.visitClub")}
              </Link>
            </div>
          </div>

          {/* Search + filters */}
          <div className={cls.toolbar}>
            <div className={cls.searchWrap}>
              <input
                className={cls.search}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("loungeJoin.searchPlaceholder")}
              />
            </div>

            <div className={cls.filters}>
              <button
                type="button"
                className={`${cls.filterBtn} ${
                  filter === "all" ? cls.filterActive : ""
                }`}
                onClick={() => setFilter("all")}
              >
                {t("loungeJoin.filters.all")}{" "}
                <span className={cls.count}>{counts.all}</span>
              </button>

              <button
                type="button"
                className={`${cls.filterBtn} ${
                  filter === "pending" ? cls.filterActive : ""
                }`}
                onClick={() => setFilter("pending")}
              >
                {t("loungeJoin.filters.pending")}{" "}
                <span className={cls.count}>{counts.pending}</span>
              </button>

              <button
                type="button"
                className={`${cls.filterBtn} ${
                  filter === "accepted" ? cls.filterActive : ""
                }`}
                onClick={() => setFilter("accepted")}
              >
                {t("loungeJoin.filters.accepted")}{" "}
                <span className={cls.count}>{counts.accepted}</span>
              </button>

              <button
                type="button"
                className={`${cls.filterBtn} ${
                  filter === "rejected" ? cls.filterActive : ""
                }`}
                onClick={() => setFilter("rejected")}
              >
                {t("loungeJoin.filters.rejected")}{" "}
                <span className={cls.count}>{counts.rejected}</span>
              </button>
            </div>
          </div>
        </Motion.header>

        {/* Errors */}
        {err ? <div className={cls.errBox}>{err}</div> : null}

        {/* List */}
        <section className={cls.list}>
          {filtered.length ? (
            filtered.map((r) => {
              const status = String(r?.status || "");
              const pillTone = tone(status);

              const sender = r?.sender || {};
              const decided = r?.decidedBy || null;

              const avatarUrl = sender?.avatar?.url || "";
              const senderName =
                sender?.username || t("loungeJoin.senderFallback");

              const updatedAt = fmtDate(r?.lastUpdatedAt);
              const decidedName = decided?.username || "-";

              const joinId = r?.clubJoinRequestId;
              const openPath = `/lounge/clubs/single/${clubId}/membership-requests/${joinId}`;

              const profilePath =
                sender?.profilePath ||
                (sender?.memberId
                  ? `/lounge/member-profile/${sender.memberId}`
                  : null);

              return (
                <Motion.article
                  key={joinId}
                  className={cls.rowCard}
                  variants={fadeUp}
                  initial="hidden"
                  animate="show"
                >
                  <Link to={openPath} className={cls.rowMain}>
                    <div className={cls.avatarWrap}>
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt=""
                          className={cls.avatar}
                          draggable="false"
                        />
                      ) : (
                        <div className={cls.avatarFallback} />
                      )}
                    </div>

                    <div className={cls.rowContent}>
                      <div className={cls.rowTop}>
                        <div className={cls.senderName}>{senderName}</div>

                        <div
                          className={`${cls.statusPill} ${
                            cls[`status_${pillTone}`] || ""
                          }`}
                        >
                          {t(
                            `loungeJoin.status.${String(status).toLowerCase()}`,
                            { defaultValue: status || "—" },
                          )}
                        </div>
                      </div>

                      <div className={cls.rowMeta}>
                        <span className={cls.metaPill}>
                          {t("loungeJoin.updatedAt")}: {updatedAt}
                        </span>

                        {String(status).toLowerCase() === "accepted" ||
                        String(status).toLowerCase() === "rejected" ? (
                          <span className={cls.metaPill}>
                            {t("loungeJoin.decidedBy")}: {decidedName}
                          </span>
                        ) : (
                          <span className={cls.metaPill}>
                            {t("loungeJoin.awaitingDecision")}
                          </span>
                        )}
                      </div>

                      {r?.message ? (
                        <p className={cls.preview}>
                          {String(r.message).slice(0, 120)}
                          {String(r.message).length > 120 ? "…" : ""}
                        </p>
                      ) : null}
                    </div>
                  </Link>

                  <div className={cls.rowActions}>
                    <Link
                      to={openPath}
                      className={`${cls.miniBtn} ${cls.miniBtnPrimary}`}
                    >
                      {t("loungeJoin.open")}
                    </Link>

                    {profilePath ? (
                      <Link
                        to={profilePath}
                        className={`${cls.miniBtn} ${cls.miniBtnGhost}`}
                      >
                        {t("loungeJoin.profile")}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className={`${cls.miniBtn} ${cls.miniBtnGhost}`}
                        disabled
                      >
                        {t("loungeJoin.profile")}
                      </button>
                    )}
                  </div>
                </Motion.article>
              );
            })
          ) : (
            <div className={cls.empty}>
              <div className={cls.emptyTitle}>{t("loungeJoin.emptyTitle")}</div>
              <p className={cls.emptyText}>{t("loungeJoin.emptyText")}</p>
              <div className={cls.emptyBtns}>
                <button
                  type="button"
                  className={`${cls.nuBtn} ${cls.nuBtnGhost}`}
                  onClick={() => {
                    setQ("");
                    setFilter("all");
                  }}
                >
                  {t("loungeJoin.reset")}
                </button>
                <Link
                  to={`/lounge/clubs/single/${clubId}`}
                  className={`${cls.nuBtn} ${cls.nuBtnInfo}`}
                >
                  {t("loungeJoin.visitClub")}
                </Link>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default LoungeJoin;
